<template>
  <v-app class="custom__bg mt-1">
    <v-container>
      <!-- Hero Header -->
      <v-row class="mb-4 animate-fadeInUp">
        <v-col cols="12">
          <v-card class="premium-card overflow-hidden" style="background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)">
            <v-card-text class="white--text pa-6">
              <v-row align="center">
                <v-col cols="12" md="7">
                  <h1 style="font-size: 30px; font-weight: 800; letter-spacing: -0.5px" class="mb-2">
                    📚 janguAsk Quiz Bank
                  </h1>
                  <p style="font-size: 15px; opacity: 0.85" class="mb-4">
                    Test your knowledge across 10 popular subjects. Challenge yourself with real exam-style questions for WAEC, JAMB, NECO & University courses.
                  </p>
                  <v-btn rounded color="white" class="text-capitalize font-weight-bold px-6 mr-3 hover-scale" style="color: #1e3c72" elevation="2" @click="startRandomQuiz">
                    <v-icon left>mdi-shuffle-variant</v-icon> Random Quiz
                  </v-btn>
                  <v-btn rounded outlined color="white" class="text-capitalize hover-scale px-6" @click="createQuizDialog = true">
                    <v-icon left>mdi-plus</v-icon> Create Quiz
                  </v-btn>
                </v-col>
                <v-col cols="12" md="5" class="text-right hidden-sm-and-down">
                  <v-row justify="end" no-gutters>
                    <v-col cols="4" class="text-center px-2">
                      <div class="font-weight-bold" style="font-size: 28px">5,432</div>
                      <div class="caption text-uppercase" style="opacity: 0.8; letter-spacing: 1px">Quizzes</div>
                    </v-col>
                    <v-col cols="4" class="text-center px-2" style="border-left: 1px solid rgba(255,255,255,0.2)">
                      <div class="font-weight-bold" style="font-size: 28px">{{ $root.storeState.stats.quizzes }}</div>
                      <div class="caption text-uppercase" style="opacity: 0.8; letter-spacing: 1px">Yours</div>
                    </v-col>
                    <v-col cols="4" class="text-center px-2" style="border-left: 1px solid rgba(255,255,255,0.2)">
                      <div class="font-weight-bold" style="font-size: 28px">{{ $root.storeState.stats.score }}%</div>
                      <div class="caption text-uppercase" style="opacity: 0.8; letter-spacing: 1px">Avg Score</div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Subject Filter Tabs -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card class="premium-card pa-2">
            <v-chip-group v-model="selectedSubject" mandatory active-class="orange darken-1 white--text" class="px-2">
              <v-chip filter v-for="s in subjects" :key="s.name" :value="s.name" class="mr-1 mb-1 font-weight-medium" style="font-size: 13px">
                <v-icon left small>{{ s.icon }}</v-icon>
                {{ s.name }}
              </v-chip>
            </v-chip-group>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quiz Cards Grid -->
      <v-row>
        <v-col
          cols="12" sm="6" md="4" lg="3"
          v-for="(quiz, i) in filteredQuizzes"
          :key="quiz.id"
          class="animate-fadeInUp"
          :class="'delay-' + ((i % 4) + 1)"
        >
          <v-card class="premium-card hover-scale fill-height d-flex flex-column" style="border-radius: 16px; cursor: pointer" @click="openQuiz(quiz)">
            <div class="pa-4 pb-2 d-flex align-center" :style="'background: linear-gradient(135deg, ' + quiz.colorA + ' 0%, ' + quiz.colorB + ' 100%); border-radius: 16px 16px 0 0'">
              <v-icon size="40" color="white">{{ quiz.icon }}</v-icon>
              <div class="ml-3">
                <div class="white--text font-weight-bold" style="font-size: 14px">{{ quiz.subject }}</div>
                <div class="white--text caption" style="opacity: 0.8">{{ quiz.level }}</div>
              </div>
            </div>
            <v-card-text class="flex-grow-1 pb-1">
              <h3 style="font-size: 15px; font-weight: 700; color: #333; line-height: 1.4" class="mb-2">{{ quiz.title }}</h3>
              <div class="d-flex align-center flex-wrap mb-2">
                <v-chip x-small color="#f0f2f5" class="mr-1 mb-1 font-weight-medium" style="font-size: 11px">{{ quiz.questions.length }} Questions</v-chip>
                <v-chip x-small color="#f0f2f5" class="mr-1 mb-1 font-weight-medium" style="font-size: 11px">{{ quiz.duration }} mins</v-chip>
                <v-chip x-small :color="diffColor(quiz.difficulty)" class="mb-1 font-weight-medium white--text" style="font-size: 11px">{{ quiz.difficulty }}</v-chip>
              </div>
              <div class="d-flex align-center grey--text" style="font-size: 12px">
                <v-avatar size="18" class="mr-1"><img :src="quiz.avatar" /></v-avatar>
                {{ quiz.author }}
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-btn block rounded color="#1e3c72" dark class="text-capitalize font-weight-bold hover-scale elevation-1" @click.stop="openQuiz(quiz)">
                <v-icon left small>mdi-play-circle</v-icon> Start Quiz
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Active Quiz Dialog -->
    <v-dialog v-model="quizDialog" max-width="750" persistent>
      <v-card class="premium-card" v-if="activeQuiz">
        <!-- Quiz Header -->
        <div class="pa-5 pb-3" :style="'background: linear-gradient(135deg, ' + activeQuiz.colorA + ' 0%, ' + activeQuiz.colorB + ' 100%)'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="white--text font-weight-bold" style="font-size: 18px">{{ activeQuiz.title }}</div>
              <div class="white--text caption" style="opacity: 0.8">{{ activeQuiz.subject }} • {{ activeQuiz.level }}</div>
            </div>
            <div class="text-right">
              <div class="white--text font-weight-bold" style="font-size: 22px">{{ questionIndex + 1 }} / {{ activeQuiz.questions.length }}</div>
              <div class="white--text caption" style="opacity: 0.8">Questions</div>
            </div>
          </div>
          <v-progress-linear :value="(questionIndex / activeQuiz.questions.length) * 100" color="white" background-color="rgba(255,255,255,0.3)" rounded class="mt-3" height="6"></v-progress-linear>
        </div>

        <v-card-text class="pa-6" v-if="!quizFinished">
          <!-- Question -->
          <h3 style="font-size: 18px; font-weight: 700; color: #222; line-height: 1.5" class="mb-5">
            Q{{ questionIndex + 1 }}. {{ currentQuestion.q }}
          </h3>

          <!-- Options -->
          <div
            v-for="(opt, oi) in currentQuestion.opts"
            :key="oi"
            class="mb-3 rounded-lg pa-4 d-flex align-center option-item"
            :class="getOptionClass(oi)"
            @click="selectOption(oi)"
            style="cursor: pointer; transition: all 0.3s ease; border: 2px solid transparent"
          >
            <div class="option-letter mr-3 d-flex align-center justify-center rounded-circle font-weight-bold" :class="getLetterClass(oi)" style="width: 32px; height: 32px; min-width: 32px; font-size: 14px">
              {{ ['A', 'B', 'C', 'D'][oi] }}
            </div>
            <span style="font-size: 15px">{{ opt }}</span>
            <v-spacer></v-spacer>
            <v-icon v-if="showAnswer && oi === currentQuestion.ans" color="green">mdi-check-circle</v-icon>
            <v-icon v-if="showAnswer && oi === selectedOption && oi !== currentQuestion.ans" color="red">mdi-close-circle</v-icon>
          </div>

          <!-- Explanation -->
          <v-alert v-if="showAnswer && currentQuestion.explanation" type="info" color="#1e3c72" icon="mdi-lightbulb-on-outline" outlined class="mt-4" style="border-radius: 12px">
            <strong>Explanation:</strong> {{ currentQuestion.explanation }}
          </v-alert>
        </v-card-text>

        <!-- Quiz Finished Screen -->
        <v-card-text class="pa-8 text-center" v-if="quizFinished">
          <v-icon size="80" :color="scoreColor">{{ scoreIcon }}</v-icon>
          <h2 class="mt-4" style="font-size: 28px; font-weight: 800">{{ scoreMessage }}</h2>
          <p class="grey--text mt-2 mb-4">You scored <strong style="font-size: 22px; color: #1e3c72">{{ correctCount }}/{{ activeQuiz.questions.length }}</strong> correct answers</p>
          <v-progress-circular :value="scorePercent" size="100" width="10" :color="scoreColor" class="mb-4">
            <span class="font-weight-bold" style="font-size: 20px">{{ scorePercent }}%</span>
          </v-progress-circular>
          <div class="d-flex justify-center mt-4">
            <v-btn rounded color="#1e3c72" dark class="text-capitalize mr-3 hover-scale" @click="retakeQuiz">
              <v-icon left>mdi-replay</v-icon> Retake
            </v-btn>
            <v-btn rounded outlined color="grey darken-1" class="text-capitalize hover-scale" @click="closeQuiz">
              Close
            </v-btn>
          </div>
        </v-card-text>

        <v-divider v-if="!quizFinished"></v-divider>
        <v-card-actions v-if="!quizFinished" class="pa-4 bg-light">
          <v-btn text class="text-capitalize" rounded @click="closeQuiz">Exit</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="!showAnswer" color="#1e3c72" dark rounded class="text-capitalize px-6 hover-scale" :disabled="selectedOption === null" @click="submitAnswer">
            Submit Answer
          </v-btn>
          <v-btn v-else color="#f69227" dark rounded class="text-capitalize px-6 hover-scale" @click="nextQuestion">
            {{ nextBtnLabel }}
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Quiz Dialog -->
    <v-dialog v-model="createQuizDialog" max-width="500">
      <v-card class="premium-card pa-2">
        <v-card-title class="d-flex align-center py-4 px-5">
          <span style="font-size: 18px; font-weight: 700">Create a Quiz</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="createQuizDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-5">
          <v-select label="Subject" :items="subjects.map(s => s.name)" outlined dense rounded color="#f69227" class="mb-3"></v-select>
          <v-text-field label="Quiz Title" outlined dense rounded color="#f69227" class="mb-3"></v-text-field>
          <v-select label="Difficulty" :items="['Easy', 'Medium', 'Hard']" outlined dense rounded color="#f69227" class="mb-3"></v-select>
          <v-select label="Duration (minutes)" :items="[5, 10, 15, 20, 30]" outlined dense rounded color="#f69227"></v-select>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text rounded class="text-capitalize" @click="createQuizDialog = false">Cancel</v-btn>
          <v-btn rounded color="#f69227" dark class="text-capitalize px-6 hover-scale" @click="createQuiz">
            Create & Add Questions
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
export default {
  data: () => ({
    selectedSubject: 'All',
    quizDialog: false,
    createQuizDialog: false,
    activeQuiz: null,
    questionIndex: 0,
    selectedOption: null,
    showAnswer: false,
    correctCount: 0,
    quizFinished: false,
    subjects: [
      { name: 'All', icon: 'mdi-view-grid' },
      { name: 'Mathematics', icon: 'mdi-calculator' },
      { name: 'English', icon: 'mdi-alphabetical' },
      { name: 'Physics', icon: 'mdi-atom' },
      { name: 'Chemistry', icon: 'mdi-flask-outline' },
      { name: 'Biology', icon: 'mdi-dna' },
      { name: 'Economics', icon: 'mdi-chart-line' },
      { name: 'Government', icon: 'mdi-domain' },
      { name: 'Computer Science', icon: 'mdi-laptop' },
      { name: 'Geography', icon: 'mdi-earth' },
      { name: 'Literature', icon: 'mdi-book-open-page-variant' },
    ],
    quizzes: [
      // === MATHEMATICS ===
      {
        id: 'q1', subject: 'Mathematics', level: 'JAMB/UTME', title: 'Algebra & Equations (JAMB 2023)',
        duration: 15, difficulty: 'Medium', icon: 'mdi-calculator',
        colorA: '#f69227', colorB: '#e65100',
        avatar: 'https://randomuser.me/api/portraits/men/55.jpg', author: 'Prof. Ibrahim Musa',
        questions: [
          { q: 'Solve for x: 3x + 7 = 22', opts: ['3', '5', '7', '9'], ans: 1, explanation: '3x = 22 - 7 = 15, so x = 15/3 = 5' },
          { q: 'If 2x² - 5x + 2 = 0, find the values of x.', opts: ['x = 2 or x = ½', 'x = 3 or x = ½', 'x = -2 or x = ½', 'x = 2 or x = -½'], ans: 0, explanation: 'Factorise: (2x-1)(x-2)=0, so x=½ or x=2' },
          { q: 'Find the sum of the first 20 terms of the AP: 3, 7, 11...', opts: ['820', '760', '840', '780'], ans: 0, explanation: 'a=3, d=4. S₂₀ = 20/2 × [2(3) + 19(4)] = 10 × 82 = 820' },
          { q: 'What is the LCM of 12, 15, and 20?', opts: ['60', '30', '120', '180'], ans: 0, explanation: '12=2²×3, 15=3×5, 20=2²×5. LCM=2²×3×5=60' },
          { q: 'If log₂8 = x, find x.', opts: ['2', '3', '4', '1'], ans: 1, explanation: '2³ = 8, so log₂8 = 3' },
        ]
      },
      {
        id: 'q2', subject: 'Mathematics', level: 'WAEC/NECO', title: 'Trigonometry & Geometry (WAEC)',
        duration: 20, difficulty: 'Hard', icon: 'mdi-calculator',
        colorA: '#e91e63', colorB: '#880e4f',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg', author: 'Dr. Amina Bello',
        questions: [
          { q: 'sin²θ + cos²θ = ?', opts: ['0', '1', '2', '1/2'], ans: 1, explanation: 'This is a fundamental Pythagorean identity. It always equals 1.' },
          { q: 'Find the area of a triangle with base 10cm and height 8cm.', opts: ['80cm²', '40cm²', '18cm²', '160cm²'], ans: 1, explanation: 'Area = ½ × base × height = ½ × 10 × 8 = 40cm²' },
          { q: 'What is the value of cos 60°?', opts: ['√3/2', '1/2', '√2/2', '1'], ans: 1, explanation: 'cos 60° = 0.5 = 1/2. A standard trigonometric value to memorize.' },
          { q: 'The sum of angles in a pentagon is:', opts: ['360°', '540°', '720°', '180°'], ans: 1, explanation: 'Sum of interior angles = (n-2)×180 = (5-2)×180 = 540°' },
        ]
      },
      // === ENGLISH ===
      {
        id: 'q3', subject: 'English', level: 'WAEC/UTME', title: 'Grammar & Comprehension',
        duration: 10, difficulty: 'Easy', icon: 'mdi-alphabetical',
        colorA: '#4caf50', colorB: '#1b5e20',
        avatar: 'https://randomuser.me/api/portraits/women/28.jpg', author: 'Mrs. Fatima Usman',
        questions: [
          { q: 'Choose the correct spelling:', opts: ['Accomodation', 'Accommodation', 'Accommadation', 'Acomodation'], ans: 1, explanation: 'The correct spelling is "Accommodation" with double c and double m.' },
          { q: 'Identify the noun in: "The teacher explained the lesson clearly."', opts: ['explained', 'clearly', 'teacher', 'the'], ans: 2, explanation: '"Teacher" is a common noun referring to a person.' },
          { q: 'The plural of "ox" is:', opts: ['oxen', 'oxes', 'ox', 'oxs'], ans: 0, explanation: '"Oxen" is an irregular plural, one of a few English nouns that form plurals this way.' },
          { q: 'Choose the correct sentence:', opts: ['He don\'t like rice.', 'He doesn\'t likes rice.', 'He doesn\'t like rice.', 'He don\'t likes rice.'], ans: 2, explanation: 'With third-person singular "he", use "doesn\'t" and the base form of the verb.' },
          { q: 'Which word is a synonym for "benevolent"?', opts: ['cruel', 'kind', 'brave', 'lazy'], ans: 1, explanation: '"Benevolent" means showing kindness or goodwill.' },
        ]
      },
      // === PHYSICS ===
      {
        id: 'q4', subject: 'Physics', level: 'JAMB/UTME', title: 'Mechanics & Motion (JAMB)',
        duration: 15, difficulty: 'Medium', icon: 'mdi-atom',
        colorA: '#9c27b0', colorB: '#4a148c',
        avatar: 'https://randomuser.me/api/portraits/men/77.jpg', author: 'Dr. Suleiman Adamu',
        questions: [
          { q: 'What is the SI unit of force?', opts: ['Joule', 'Newton', 'Watt', 'Pascal'], ans: 1, explanation: 'Force is measured in Newtons (N). Named after Sir Isaac Newton. F = ma.' },
          { q: 'A body moves with velocity 20m/s and decelerates at 4m/s². Time to stop?', opts: ['3s', '4s', '5s', '6s'], ans: 2, explanation: 'v = u + at → 0 = 20 + (-4)t → t = 20/4 = 5s' },
          { q: 'The acceleration due to gravity g ≈', opts: ['9.0 m/s²', '9.8 m/s²', '10.8 m/s²', '8.9 m/s²'], ans: 1, explanation: 'Standard value of gravitational acceleration on Earth\'s surface is 9.8 m/s².' },
          { q: 'Work done = Force × ?', opts: ['time', 'mass', 'distance', 'velocity'], ans: 2, explanation: 'W = F × d (distance in the direction of force). Unit is Joules (J).' },
          { q: 'Which principle states that a body immersed in fluid experiences upthrust equal to fluid displaced?', opts: ['Pascal\'s', 'Newton\'s', 'Archimedes\'', 'Bernoulli\'s'], ans: 2, explanation: 'Archimedes\' Principle. The upthrust equals weight of fluid displaced.' },
        ]
      },
      // === CHEMISTRY ===
      {
        id: 'q5', subject: 'Chemistry', level: 'SS3/WAEC', title: 'Organic Chemistry Basics',
        duration: 15, difficulty: 'Medium', icon: 'mdi-flask-outline',
        colorA: '#009688', colorB: '#004d40',
        avatar: 'https://randomuser.me/api/portraits/women/65.jpg', author: 'Grace Okafor',
        questions: [
          { q: 'What is the general formula for alkanes?', opts: ['CₙH₂ₙ', 'CₙH₂ₙ₋₂', 'CₙH₂ₙ₊₂', 'CₙHₙ'], ans: 2, explanation: 'Alkanes are saturated hydrocarbons with formula CₙH₂ₙ₊₂. e.g. methane CH₄ (n=1).' },
          { q: 'Saponification is the process of making:', opts: ['Plastics', 'Soap', 'Petrol', 'Fertiliser'], ans: 1, explanation: 'Saponification is the hydrolysis of fats/oils with alkali (NaOH) to make soap.' },
          { q: 'What is the valency of Carbon?', opts: ['2', '4', '6', '3'], ans: 1, explanation: 'Carbon has 4 valence electrons and forms 4 covalent bonds. Valency = 4.' },
          { q: 'Which of these is a noble gas?', opts: ['Nitrogen', 'Oxygen', 'Argon', 'Hydrogen'], ans: 2, explanation: 'Argon (Ar) is a noble gas in Group 18, with a full outer electron shell.' },
          { q: 'The pH of a neutral solution is:', opts: ['0', '7', '14', '1'], ans: 1, explanation: 'A neutral solution has pH 7. Below 7 is acidic, above 7 is basic/alkaline.' },
        ]
      },
      // === BIOLOGY ===
      {
        id: 'q6', subject: 'Biology', level: 'SS2/WAEC', title: 'Cells & Genetics (WAEC/JAMB)',
        duration: 15, difficulty: 'Medium', icon: 'mdi-dna',
        colorA: '#2196f3', colorB: '#0d47a1',
        avatar: 'https://randomuser.me/api/portraits/men/22.jpg', author: 'Mr. Yusuf Aliyu',
        questions: [
          { q: 'What is the powerhouse of the cell?', opts: ['Nucleus', 'Ribosome', 'Mitochondria', 'Vacuole'], ans: 2, explanation: 'Mitochondria produce ATP through cellular respiration. They\'re called the powerhouse of the cell.' },
          { q: 'DNA stands for:', opts: ['Dioxyribo Nucleic Acid', 'Deoxyribonucleic Acid', 'Dinucleic Acid', 'Disoribo Nucleic Acid'], ans: 1, explanation: 'DNA = Deoxyribonucleic Acid, the molecule that carries genetic information.' },
          { q: 'How many chambers does the human heart have?', opts: ['2', '3', '4', '6'], ans: 2, explanation: 'The human heart has 4 chambers: right atrium, right ventricle, left atrium, left ventricle.' },
          { q: 'Osmosis is the movement of water from:', opts: ['Low to high concentration', 'High to low concentration', 'High to higher concentration', 'Low solute to high solute'], ans: 1, explanation: 'Osmosis: water moves from a region of high water concentration (low solute) to low water concentration through a semipermeable membrane.' },
          { q: 'Which blood group is the universal donor?', opts: ['A', 'B', 'AB', 'O'], ans: 3, explanation: 'Blood group O negative is the universal donor because it has no A or B antigens.' },
        ]
      },
      // === ECONOMICS ===
      {
        id: 'q7', subject: 'Economics', level: 'SS3/WAEC', title: 'Demand, Supply & Market',
        duration: 10, difficulty: 'Easy', icon: 'mdi-chart-line',
        colorA: '#ff9800', colorB: '#e65100',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg', author: 'Dr. Amina Bello',
        questions: [
          { q: 'The law of demand states that as price increases, quantity demanded:', opts: ['Increases', 'Decreases', 'Stays the same', 'Doubles'], ans: 1, explanation: 'Inverse relationship between price and quantity demanded, ceteris paribus.' },
          { q: 'What causes a shift in the supply curve?', opts: ['Change in price of the good', 'Change in technology', 'Change in quantity demanded', 'Both A and B'], ans: 1, explanation: 'Movement along the supply curve = price change. Shift = non-price factors like technology, input costs.' },
          { q: 'GDP stands for:', opts: ['General Development Product', 'Gross Domestic Product', 'Global Development Project', 'Gross Demand Product'], ans: 1, explanation: 'GDP is the total monetary value of all goods and services produced within a country in a year.' },
          { q: 'Inflation is defined as:', opts: ['Rising unemployment', 'A sustained rise in the general price level', 'Falling GDP', 'A budget surplus'], ans: 1, explanation: 'Inflation is a persistent increase in the general price level of goods and services in an economy.' },
        ]
      },
      // === GOVERNMENT ===
      {
        id: 'q8', subject: 'Government', level: 'SS3/WAEC', title: 'Nigerian Government & Politics',
        duration: 10, difficulty: 'Easy', icon: 'mdi-domain',
        colorA: '#607d8b', colorB: '#263238',
        avatar: 'https://randomuser.me/api/portraits/men/36.jpg', author: 'Mr. Muhammad Bello',
        questions: [
          { q: 'Nigeria became a federal republic in:', opts: ['1960', '1963', '1966', '1979'], ans: 1, explanation: 'Nigeria became a Federal Republic on October 1, 1963, replacing the Queen as head of state.' },
          { q: 'Separation of powers was proposed by:', opts: ['John Locke', 'Jean-Jacques Rousseau', 'Montesquieu', 'Hobbes'], ans: 2, explanation: 'Montesquieu, in "The Spirit of the Laws" (1748), proposed separating legislative, executive, and judicial powers.' },
          { q: 'The Nigerian Senate has how many members?', opts: ['360', '109', '468', '360'], ans: 1, explanation: 'The Nigerian Senate has 109 members — 3 senators from each of the 36 states, plus 1 from FCT.' },
          { q: 'What is suffrage?', opts: ['The right to vote', 'The right to protest', 'The right to free speech', 'The right to own land'], ans: 0, explanation: 'Suffrage is the right to vote in political elections.' },
        ]
      },
      // === COMPUTER SCIENCE ===
      {
        id: 'q9', subject: 'Computer Science', level: 'Tertiary/JAMB', title: 'Programming & Data Structures',
        duration: 15, difficulty: 'Hard', icon: 'mdi-laptop',
        colorA: '#1e3c72', colorB: '#2a5298',
        avatar: 'https://randomuser.me/api/portraits/women/68.jpg', author: 'Dr. Ngozi Emmanuel',
        questions: [
          { q: 'What does HTML stand for?', opts: ['Hyper Text Markup Language', 'High Tech Modern Language', 'Hyper Transfer Markup Language', 'Home Tool Markup Language'], ans: 0, explanation: 'HTML = HyperText Markup Language. It is the standard language for creating web pages.' },
          { q: 'Which data structure operates on FIFO principle?', opts: ['Stack', 'Queue', 'Tree', 'Graph'], ans: 1, explanation: 'Queue follows First-In, First-Out (FIFO). Like a line at a bank — first to enter is first to leave.' },
          { q: 'What is the time complexity of binary search?', opts: ['O(n)', 'O(n²)', 'O(log n)', 'O(1)'], ans: 2, explanation: 'Binary search is O(log n) — it halves the search space with each comparison.' },
          { q: '0 + 1 in binary = ?', opts: ['1', '10', '11', '0'], ans: 0, explanation: '0 + 1 = 1 in binary. Binary uses only 0 and 1.' },
          { q: 'An operating system is a:', opts: ['Hardware', 'Application Software', 'System Software', 'Utility Program'], ans: 2, explanation: 'An OS is system software that manages hardware resources and provides services for computer programs.' },
        ]
      },
      // === GEOGRAPHY ===
      {
        id: 'q10', subject: 'Geography', level: 'SS2/WAEC', title: 'Physical Geography of West Africa',
        duration: 10, difficulty: 'Easy', icon: 'mdi-earth',
        colorA: '#795548', colorB: '#3e2723',
        avatar: 'https://randomuser.me/api/portraits/men/45.jpg', author: 'Mr. Umar Abubakar',
        questions: [
          { q: 'The largest country in Africa by land area is:', opts: ['Nigeria', 'Sudan', 'Algeria', 'Congo'], ans: 2, explanation: 'Algeria is the largest country in Africa with an area of approximately 2.38 million km².' },
          { q: 'What type of climate is dominant in northern Nigeria?', opts: ['Tropical rainforest', 'Mediterranean', 'Semi-arid / Savanna', 'Tundra'], ans: 2, explanation: 'Northern Nigeria has a semi-arid climate with distinct wet and dry seasons.' },
          { q: 'The River Niger flows into which ocean?', opts: ['Indian Ocean', 'Atlantic Ocean', 'Arctic Ocean', 'Pacific Ocean'], ans: 1, explanation: 'The Niger River flows southward through Nigeria and empties into the Atlantic Ocean via the Niger Delta.' },
          { q: 'What is the capital of Nigeria?', opts: ['Lagos', 'Kano', 'Abuja', 'Port Harcourt'], ans: 2, explanation: 'Abuja became the capital of Nigeria in 1991, replacing Lagos.' },
        ]
      },
      // === LITERATURE ===
      {
        id: 'q11', subject: 'Literature', level: 'SS3/WAEC', title: 'African Literature & Prose',
        duration: 10, difficulty: 'Medium', icon: 'mdi-book-open-page-variant',
        colorA: '#673ab7', colorB: '#311b92',
        avatar: 'https://randomuser.me/api/portraits/women/52.jpg', author: 'Mrs. Aisha Mohammed',
        questions: [
          { q: 'Who wrote "Things Fall Apart"?', opts: ['Wole Soyinka', 'Chinua Achebe', 'Ngugi wa Thiong\'o', 'Ben Okri'], ans: 1, explanation: '"Things Fall Apart" (1958) was written by Chinua Achebe. It is one of the most important African novels.' },
          { q: 'In which year did Wole Soyinka win the Nobel Prize for Literature?', opts: ['1978', '1986', '1990', '2000'], ans: 1, explanation: 'Wole Soyinka won the Nobel Prize for Literature in 1986, the first African to do so.' },
          { q: '"Half of a Yellow Sun" is a novel by:', opts: ['Flora Nwapa', 'Buchi Emecheta', 'Chimamanda Ngozi Adichie', 'Ama Ata Aidoo'], ans: 2, explanation: '"Half of a Yellow Sun" was written by Chimamanda Ngozi Adichie, set during the Biafran War.' },
          { q: 'A sonnet has how many lines?', opts: ['10', '12', '14', '16'], ans: 2, explanation: 'A sonnet is a 14-line poem, typically in iambic pentameter, popularised by Shakespeare and Petrarch.' },
        ]
      },
    ]
  }),
  computed: {
    filteredQuizzes() {
      if (this.selectedSubject === 'All') return this.quizzes;
      return this.quizzes.filter(q => q.subject === this.selectedSubject);
    },
    currentQuestion() {
      if (!this.activeQuiz) return null;
      return this.activeQuiz.questions[this.questionIndex];
    },
    nextBtnLabel() {
      if (!this.activeQuiz) return 'Next';
      return this.questionIndex < this.activeQuiz.questions.length - 1 ? 'Next Question' : 'See Results';
    },
    scorePercent() {
      if (!this.activeQuiz) return 0;
      return Math.round((this.correctCount / this.activeQuiz.questions.length) * 100);
    },
    scoreMessage() {
      if (this.scorePercent >= 80) return '🏆 Excellent!';
      if (this.scorePercent >= 60) return '👏 Good Job!';
      if (this.scorePercent >= 40) return '📖 Keep Studying!';
      return '💪 Try Again!';
    },
    scoreColor() {
      if (this.scorePercent >= 80) return 'green';
      if (this.scorePercent >= 60) return 'orange';
      return 'red';
    },
    scoreIcon() {
      if (this.scorePercent >= 80) return 'mdi-trophy';
      if (this.scorePercent >= 60) return 'mdi-thumb-up';
      return 'mdi-book-open-page-variant';
    }
  },
  methods: {
    diffColor(diff) {
      if (diff === 'Easy') return 'green';
      if (diff === 'Medium') return 'orange darken-1';
      return 'red darken-1';
    },
    openQuiz(quiz) {
      this.activeQuiz = quiz;
      this.questionIndex = 0;
      this.selectedOption = null;
      this.showAnswer = false;
      this.correctCount = 0;
      this.quizFinished = false;
      this.quizDialog = true;
    },
    startRandomQuiz() {
      const random = this.quizzes[Math.floor(Math.random() * this.quizzes.length)];
      this.openQuiz(random);
    },
    getOptionClass(oi) {
      if (!this.showAnswer) {
        return oi === this.selectedOption ? 'selected-option' : 'default-option';
      }
      if (oi === this.currentQuestion.ans) return 'correct-option';
      if (oi === this.selectedOption) return 'wrong-option';
      return 'default-option';
    },
    getLetterClass(oi) {
      if (!this.showAnswer) {
        return oi === this.selectedOption ? 'letter-selected' : 'letter-default';
      }
      if (oi === this.currentQuestion.ans) return 'letter-correct';
      if (oi === this.selectedOption) return 'letter-wrong';
      return 'letter-default';
    },
    selectOption(oi) {
      if (!this.showAnswer) this.selectedOption = oi;
    },
    submitAnswer() {
      if (this.selectedOption === null) return;
      this.showAnswer = true;
      if (this.selectedOption === this.currentQuestion.ans) {
        this.correctCount++;
      }
    },
    nextQuestion() {
      if (this.questionIndex < this.activeQuiz.questions.length - 1) {
        this.questionIndex++;
        this.selectedOption = null;
        this.showAnswer = false;
      } else {
        this.quizFinished = true;
        // update stats
        this.$root.storeState.stats.quizzes++;
        this.$root.storeState.stats.score = Math.round(
          (this.$root.storeState.stats.score + this.scorePercent) / 2
        );
        this.$root.store.save();
      }
    },
    retakeQuiz() {
      this.questionIndex = 0;
      this.selectedOption = null;
      this.showAnswer = false;
      this.correctCount = 0;
      this.quizFinished = false;
    },
    closeQuiz() {
      this.quizDialog = false;
      this.activeQuiz = null;
    },
    createQuiz() {
      this.createQuizDialog = false;
      this.$root.showSnackbar('Quiz created! Add questions in the editor.', '#43a047', 'mdi-check-circle');
    }
  }
};
</script>

<style scoped>
.custom__bg {
  background: #f5f5f5;
  min-height: 100vh;
}
.bg-light {
  background-color: #fafbfc !important;
}
.default-option {
  background-color: #f8f9fa;
  border-color: transparent !important;
}
.default-option:hover {
  background-color: #e3f2fd;
  border-color: #90caf9 !important;
}
.selected-option {
  background-color: #e3f2fd !important;
  border-color: #1e3c72 !important;
}
.correct-option {
  background-color: #e8f5e9 !important;
  border-color: #4caf50 !important;
}
.wrong-option {
  background-color: #ffebee !important;
  border-color: #ef5350 !important;
}
.letter-default {
  background-color: #e0e0e0;
  color: #555;
}
.letter-selected {
  background-color: #1e3c72;
  color: white;
}
.letter-correct {
  background-color: #4caf50;
  color: white;
}
.letter-wrong {
  background-color: #ef5350;
  color: white;
}
</style>
